import { WorkflowContext, Task } from '@/interface/workflow'
import { get, unset, cloneDeep, isEmpty, omit, intersection } from 'lodash'
import { trialStore } from '@/store'
import { compareSessionWorkflow } from '@/utils/sessionWorkflow'

// 工作流最大嵌套层级
export const TASK_MAX_LEVEL = 6

/**
 * 是否可以添加控制节点
 * 目前工作流最大嵌套层级为 TASK_MAX_LEVEL ，
 * 当层级小于 TASK_MAX_LEVEL 时，可以添加动作节点和流程节点
 * 当层级等于 TASK_MAX_LEVEL 时，只能添加动作节点
 * @param cur 当前节点
 * @param isLast 是否是最后一个节点
 * @returns 是否可以添加控制节点
 */
export const canNotAddControlNode = (cur: Task, isLast?: boolean): boolean => {
  return !!(
    cur?.levelIndex?.length === TASK_MAX_LEVEL ||
      (cur?.levelIndex?.length === TASK_MAX_LEVEL - 1 &&
        cur?.groupName === 'control' && isLast)
  )
}

/**
 * 递归处理任务，设置orderIndex和levelIndex
 * @param tasks 任务列表
 * @param parentIndices 父级索引
 * @returns 处理后的任务列表
 */
export const processTasksRecursively = (tasks: any[], parentIndices: number[] = []) => {
  let _upIndex = 0
  return tasks.map((task, index) => {
    // 设置当前任务的order索引和level索引
    const currentLevelIndex = [...parentIndices, index]
    _upIndex += 1
    const updatedTask = {
      ...task,
      orderIndex: _upIndex,
      levelIndex: currentLevelIndex
    }
    
    // 如果有子任务，递归处理
    if (updatedTask.tasks && updatedTask.tasks.length > 0) {
      // 递归处理子任务，并保持orderIndex连续
      const { tasks: processedSubTasks, lastOrderIndex } = processSubTasks(
        updatedTask.tasks, 
        currentLevelIndex, 
        _upIndex
      )
      _upIndex = lastOrderIndex
      updatedTask.tasks = processedSubTasks
      updatedTask.tasksMaxLevel = getMaxTaskLevel(processedSubTasks) + 1
    }
    
    return updatedTask
  })
}

// 处理子任务并返回最后的orderIndex
const processSubTasks = (tasks: any[], parentIndices: number[], startOrderIndex: number) => {
  let _upIndex = startOrderIndex
  const processedTasks = tasks.map((task, index) => {
    const currentLevelIndex = [...parentIndices, index]
    _upIndex += 1
    const updatedTask = {
      ...task,
      orderIndex: _upIndex,
      levelIndex: currentLevelIndex
    }
    
    // 如果有更深层级的子任务，继续递归
    if (updatedTask.tasks && updatedTask.tasks.length > 0) {
      const { tasks: processedSubTasks, lastOrderIndex } = processSubTasks(
        updatedTask.tasks, 
        currentLevelIndex, 
        _upIndex
      )
      _upIndex = lastOrderIndex
      updatedTask.tasks = processedSubTasks
    }
    
    return updatedTask
  })
  
  return { tasks: processedTasks, lastOrderIndex: _upIndex }
}

// 获取任务树的最大嵌套层级
const getMaxTaskLevel = (tasks: any[]) => {
  let maxLevel = 0
  
  tasks.forEach(task => {
    if (task.tasks && task.tasks.length > 0) {
      const subTaskLevel = getMaxTaskLevel(task.tasks) + 1
      maxLevel = Math.max(maxLevel, subTaskLevel)
    }
  })
  
  return maxLevel
}

/**
 * 根据层级索引删除任务
 * @param tasks 任务列表
 * @param indices 层级索引
 * @returns 删除后的任务列表
 */
export const removeTaskAtLevel = (tasks: any[], indices: number[]): any[] => {
  // 如果是顶层删除
  if (indices.length === 1) {
    const newTasks = [...tasks]
    newTasks.splice(indices[0], 1)
    return newTasks
  }
  
  // 获取当前级别索引和剩余索引
  const [currentIndex, ...restIndices] = indices
  
  // 修改当前任务的子任务
  return tasks.map((task, index) => {
    if (index !== currentIndex) return task
    
    // 如果剩余只有一个索引，表示要删除的是当前任务的直接子任务
    if (restIndices.length === 1) {
      const newSubTasks = [...task.tasks]
      newSubTasks.splice(restIndices[0], 1)
      return { ...task, tasks: newSubTasks }
    }
    
    // 否则递归处理更深层级
    return {
      ...task,
      tasks: removeTaskAtLevel(task.tasks || [], restIndices)
    }
  })
}


/**
 * 递归获取指定层级的tasks数组
 * @param tasks 任务列表
 * @param levelIndices 层级索引
 * @returns 获取后的任务列表
 */
export const getTasksByLevelIndex = (tasks: any[], levelIndices: number[]): any[] => {
  if (levelIndices.length === 0) return tasks
  const [currentIndex, ...restIndices] = levelIndices
  if (!tasks[currentIndex]?.tasks || restIndices.length === 0) return tasks[currentIndex]?.tasks || []
  return getTasksByLevelIndex(tasks[currentIndex].tasks, restIndices)
}

/**
 * 递归更新指定层级的tasks
 * @param tasks 任务列表
 * @param levelIndices 层级索引
 * @param newTasks 新的任务列表
 * @returns 更新后的任务列表
 */
export const updateTasksByLevelIndex = (tasks: any[], levelIndices: number[], newTasks: any[]): any[] => {
  if (levelIndices.length === 0) return newTasks
  
  const [currentIndex, ...restIndices] = levelIndices
  return tasks.map((task, index) => {
    if (index !== currentIndex) return task
    
    if (restIndices.length === 0) {
      return { ...task, tasks: newTasks }
    }
    
    return {
      ...task,
      tasks: updateTasksByLevelIndex(task.tasks || [], restIndices, newTasks)
    }
  })
}

/**
 * 递归获取指定name的节点
 * @param tasks 任务列表
 * @param name 节点名称
 * @returns 获取后的节点
 */
export const findTaskByNameDeep = (tasks: Task[], name: string): Task | null => {
  for (const task of tasks) {
    if (task.name === name) return task
    if (task.tasks) {
      const found = findTaskByNameDeep(task.tasks, name)
      if (found) return found
    }
  }
  return null
}

/**
 * 根据层级索引获取任务
 * @param tasks 任务列表
 * @param indices 层级索引
 * @returns 获取后的任务
 */
export const getTaskByLevelIndex = (tasks: any[], indices: number[]): any => {
  if (indices.length === 0) return null
  const [currentIndex, ...restIndices] = indices
  if (restIndices.length === 0) return tasks[currentIndex]
  return getTaskByLevelIndex(tasks[currentIndex]?.tasks || [], restIndices)
}

/**
 * 递归获取指定层级的任务
 * @param tasks 任务列表
 * @param indices 层级索引
 * @returns 获取后的任务
 */
export const getTaskByIndices = (tasks: any[], indices: number[]): any => {
  if (indices.length === 0 || !tasks || tasks.length === 0) return null
  const [currentIndex, ...restIndices] = indices
  if (restIndices.length === 0) return tasks[currentIndex]
  return getTaskByIndices(tasks[currentIndex]?.tasks || [], restIndices)
}

/**
 * 递归更新指定层级的任务类型
 * @param tasks 任务列表
 * @param indices 层级索引
 * @param newTask 新的任务
 * @returns 更新后的任务列表
 */
export const updateTaskAtLevel = (tasks: any[], indices: number[], newTask: any): any[] => {
  if (indices.length === 1) {
    const newTasks = [...tasks]
    newTasks.splice(indices[0], 1, newTask)
    return newTasks
  }
  
  const [currentIndex, ...restIndices] = indices
  return tasks.map((task, index) => {
    if (index !== currentIndex) return task
    
    return {
      ...task,
      tasks: updateTaskAtLevel(task.tasks || [], restIndices, newTask)
    }
  })
}

/**
 * 根据任务索引获取子任务列表
 * @param tasks 任务列表
 * @param indices 任务索引数组
 * @returns 子任务列表
 */
function getTasks(tasks: any[], indices: number[]): any[] {
  if (!indices.length) return tasks

  let currentTasks = tasks
  let currentTask: any = null

  for (const index of indices) {
    currentTask = currentTasks[index]
    if (!currentTask || !Array.isArray(currentTask.tasks)) {
      return []
    }
    currentTasks = currentTask.tasks
  }

  return currentTasks
}

/**
 * 递归获取工作流上下文中可用的 keys 和 orderIndex
 * @param tasks 任务列表
 * @param taskIndex 任务索引数组
 * @param currentDepth 当前递归深度
 * @param contextKeys 收集的上下文键数组
 * @returns 可用的上下文键数组
 */
export function getAvailableContextKeys(
  tasks: any[],
  taskIndex: number[],
  currentDepth: number = 0,
  contextKeys: Array<{ key: string; orderIndex: number }> = []
): Array<{ key: string; orderIndex: number }> {
  // 如果已经处理完所有层级或者任务索引不存在，则返回结果
  if (currentDepth >= taskIndex.length || taskIndex[currentDepth] < 0) {
    return contextKeys
  }

  // 获取当前层级的任务列表
  const currentTasks = currentDepth === 0 ? tasks : getTasks(tasks, taskIndex.slice(0, currentDepth))

  // 添加当前层级中，索引之前的所有任务
  const previousTasks = currentTasks
    .filter((_, index) => index < taskIndex[currentDepth])
    .map(item => ({ key: item.name, orderIndex: item.orderIndex }))
  
  contextKeys.push(...previousTasks)

  // 如果存在下一层级，则添加当前任务，并继续递归
  if (currentDepth + 1 < taskIndex.length && taskIndex[currentDepth + 1] >= 0) {
    const currentTask = currentTasks[taskIndex[currentDepth]]
    if (currentTask) {
      contextKeys.push({ key: currentTask.name, orderIndex: currentTask.orderIndex })
      return getAvailableContextKeys(tasks, taskIndex, currentDepth + 1, contextKeys)
    }
  }

  return contextKeys
}

/**
 * 过滤工作流上下文，只保留可用的上下文
 * @param workflow 工作流
 * @param taskIndex 任务索引数组
 * @param workflowContext 工作流上下文数组
 * @returns 过滤后的工作流上下文
 */
export function filterWorkflowContext(workflow: any, taskIndex: number[], workflowContext: any[]): any[] {
  // 获取可用的上下文键
  const contextKeys = getAvailableContextKeys(workflow.tasks, taskIndex)
  
  // 过滤上下文
  const filteredContext: WorkflowContext[] = workflowContext?.filter(item => {
    const isExist = contextKeys.find(key => key.key === item.key)
    return isExist || item.key === '$variables'
  }).map(item => {
    const foundKey = contextKeys.find(key => key.key === item.key)
    return { ...item, orderIndex: foundKey?.orderIndex }
  })
  
  return filteredContext
}

/**
 * 处理表单隐藏字段
 * @param values 表单值
 * @returns 处理后的表单值
 */
export const processHiddenFields = (values: any): any => {
  if (!Array.isArray(values?.formilyHiddenFields) || isEmpty(values?.formilyHiddenFields)) {
    return omit(values, ['formilyHiddenFields']);
  }

  const processedValues = { ...values };
  
  // 找出需要删除的字段：在 formilyHiddenFields 中存在且 values 中有值的字段
  // const fieldsToRemove = processedValues.formilyHiddenFields.filter((key: string) => has(processedValues, key));
  const fieldsToRemove = intersection(processedValues.formilyHiddenFields, Object.keys(processedValues));

  // 删除隐藏字段
  return omit(processedValues, [...fieldsToRemove, 'formilyHiddenFields']);
};

/**
 * 根据层级索引更新任务
 * 支持任意嵌套层级的任务更新
 * @param tasks 任务列表
 * @param taskIndex 层级索引
 * @param values 更新的值
 * @param type 任务类型
 */
export const updateTaskByIndexDeep = (tasks: any[], taskIndex: number[], values: any, type: string): void => {
  if (!taskIndex || taskIndex.length === 0) return;

  // 创建单据特殊处理
  if (type === 'flow-create-task') {
    const oldDetails = get(tasks, [...taskIndex, 'details'])
    // 如果当前节点没有details，而oldDetails有值, 那么删除oldDetails
    if (!values?.details && !!oldDetails) {
      unset(tasks, [...taskIndex, 'details'])
    }
  }
  
  // 递归辅助函数
  const updateTaskAtPath = (currentTasks: any[], indices: number[], depth: number = 0) => {
    const currentIndex = indices[depth];
    const currentItem = currentTasks[currentIndex];
    
    // 如果已经到达最后一级，更新节点
    if (depth === indices.length - 1) {
      let updatedItem = { ...currentItem, ...values };
      
      // 特殊处理 apportion 类型
      if (type === 'apportion' && depth === 0) {
        updatedItem = { ...values };
      }
      
      // 处理隐藏字段
      const processedValues = processHiddenFields(updatedItem);
      
      currentTasks.splice(currentIndex, 1, { ...processedValues, type, status: 'success' });
      return;
    }
    
    // 继续向下递归
    if (currentItem?.tasks) {
      updateTaskAtPath(currentItem.tasks, indices, depth + 1);
    }
  };
  
  updateTaskAtPath(tasks, taskIndex);
};

/**
 * 递归更新任务树
 * 支持无限层级的嵌套任务
 * @param tasks 源任务列表
 * @param submitTasks 目标任务列表
 */
export const updateTasksDeep = (tasks: any[], submitTasks: any[]): void => {
  if (isEmpty(tasks) || !Array.isArray(tasks)) return;
  
  tasks.forEach(task => {
    if (!task.type || !task.levelIndex) return;
    
    const values = cloneDeep(task);
    const { type, levelIndex } = task;
    
    updateTaskByIndexDeep(submitTasks, levelIndex, values, type);
    
    if (!isEmpty(task.tasks) && Array.isArray(task.tasks)) {
      updateTasksDeep(task.tasks, submitTasks);
    }
  });
};

/**
 * 处理工作流数据
 * 支持无限层级的任务节点 
 * @param workflow 工作流对象
 * @returns 处理后的工作流对象
 */
export const processWorkflowData = (workflow: any): any => {
  const submitTasks: any[] = Object.assign([], workflow.tasks); // 提交使用
  const _tasks = cloneDeep(submitTasks); // 遍历使用
  
  updateTasksDeep(_tasks, submitTasks);
  
  const _workflow = { ...workflow, tasks: submitTasks };
  
  // 更新工作流的时候，需要判断，如果当前工作流信息没有改动，那么就不更新
  if (_workflow.hasOwnProperty('id')) {
    const _dugSuccess = trialStore?.getState?.()?.dugSuccess ? 1 : 0;
    _workflow.debugState = compareSessionWorkflow(_workflow) ? _dugSuccess : 0;
  }
  
  // 添加数据限制
  _workflow.dataLimit = 20;
  
  return _workflow;
};

/**
 * 根据任务索引和类型递归更新任务
 * @param tasks 任务列表
 * @param taskIndex 任务索引数组
 * @param values 更新值
 * @param type 任务类型
 * @returns 更新后的任务列表
 */
export const updateTaskRecursively = (tasks: Task[], taskIndex: number[], values: any, type: string): any[] => {
  const updatedTasks = cloneDeep(tasks);
  
  const updateNestedTask = (currentTasks: Task[], indices: number[], currentLevel = 0) => {
    if (currentLevel === indices.length - 1) {
      const targetIndex = indices[currentLevel];
      const preItem = currentTasks[targetIndex];
      const item = { ...preItem, ...values, type, status: 'success' };
      currentTasks.splice(targetIndex, 1, item);
      return;
    }
    
    const targetIndex = indices[currentLevel];
    if (!isEmpty(currentTasks[targetIndex]?.tasks) && Array.isArray(currentTasks[targetIndex]?.tasks)) {
      updateNestedTask(currentTasks[targetIndex].tasks, indices, currentLevel + 1);
    }
  };
  
  // 开始递归更新
  if (taskIndex.length > 0) {
    updateNestedTask(updatedTasks, taskIndex);
  }
  
  return updatedTasks;
}

/**
 * 获取表单隐藏字段
 * @param form 表单对象
 * @param type 节点类型
 * @returns 隐藏字段数组
 */
export const getFormilyHiddenFields = (form: any, type: string) => {
  const formilyHiddenFields: string[] = [];
  Object.keys(form.fields).forEach(key => {
    const field = form.fields[key] || {}
    if (!(field.display === 'visible' && !field.hidden && field.visible)) {
      // 对于 aggregation 类型，display: 'hidden' 的字段保留，遵循formily显示状态定义
      if (type === 'aggregation' && field.display === 'hidden') {
        return; // 跳过这个字段，不加入隐藏字段列表
      }
      
      // 根据类型判断是否需要拆分key
      const isSplitKeyForType = ['clock'].includes(type);
      const splitKey = key.split('.');

      // 如果不需要拆分key，且key的长度大于1，跳过（例如：筛选条件 combineFilters）
      if (!isSplitKeyForType && splitKey.length > 1) return;

      const fieldKey = isSplitKeyForType ? splitKey?.[splitKey?.length - 1] : key;
      // 不能隐藏关键字段 name, type, label, desc, groupName
      if (!['name', 'type', 'levelIndex', 'orderIndex', 'label', 'desc', 'groupName'].includes(fieldKey)) {
        formilyHiddenFields.push(fieldKey);
      }
    }
  })
  return cloneDeep(formilyHiddenFields);
}

/**
 * 检查是否需要更新缓存
 * 兼容 tasksVersion 字段的向后兼容性
 * @param workflowRet 从服务器获取的工作流数据
 * @param indexDBData 本地 IndexDB 中的缓存数据
 * @returns 是否需要更新缓存
 */
export const shouldUpdateCache = (workflow: any, indexDBData: any): boolean => {
  // 如果没有本地缓存数据，不需要更新
  if (!indexDBData) {
    return false
  }

  // 检查是否有 tasksVersion 字段
  const hasServerTasksVersion = workflow?.tasksVersion === 0 || !!workflow?.tasksVersion
  const hasCacheTasksVersion = indexDBData?.data?.tasksVersion === 0 || !!indexDBData?.data?.tasksVersion

  // 如果服务器和本地缓存都有 tasksVersion 字段，使用新逻辑
  if (hasServerTasksVersion && hasCacheTasksVersion) {
    return workflow.tasksVersion > indexDBData.data.tasksVersion
  }

  // 如果服务器没有 tasksVersion，使用历史逻辑：比对 updateTime
  const serverUpdateTime = workflow?.updateTime ? new Date(workflow.updateTime).getTime() : 0
  const cacheUpdateTime = indexDBData?.data?.updateTime ? new Date(indexDBData.data.updateTime).getTime() : 0

  return serverUpdateTime > cacheUpdateTime
}